import type {
  ComparableSet,
  MetricGroup,
  MetricOrigin,
  MetricQuality,
  MetricView,
  ValuationConfidence,
  ValuationMethod,
  ValuationWarning,
} from '@mfos/shared';
import type { BadgeTone } from '../../components/Badge';
import { formatDate, formatPercent } from '../../lib/format';
import { formatAmount } from '../finance/finance-display';

interface Display {
  readonly label: string;
  readonly tone: BadgeTone;
}

export const valuationBase = (workspaceId: string) => `/w/${workspaceId}/finance/valuation`;

export function valuationRef(number: number): string {
  return `VAL-${String(number)}`;
}

export const METHOD_LABELS: Readonly<Record<ValuationMethod, string>> = {
  REVENUE_MULTIPLE: 'Revenue multiple',
  ARR_MULTIPLE: 'ARR multiple',
  EBITDA_MULTIPLE: 'EBITDA multiple (operating result)',
};

export const CONFIDENCE: Readonly<Record<ValuationConfidence, Display>> = {
  HIGH: { label: 'High confidence', tone: 'ok' },
  MEDIUM: { label: 'Medium confidence', tone: 'warn' },
  LOW: { label: 'Low confidence', tone: 'down' },
};

export const QUALITY: Readonly<Record<MetricQuality, Display>> = {
  OBSERVED: { label: 'From records', tone: 'ok' },
  MANUAL: { label: 'Entered', tone: 'info' },
  ESTIMATED: { label: 'Estimate', tone: 'warn' },
  STALE: { label: 'Out of date', tone: 'warn' },
  MISSING: { label: 'Missing', tone: 'down' },
};

export const ORIGIN_LABELS: Readonly<Record<MetricOrigin, string>> = {
  BOOKS: 'Books',
  FOUNDER_RECORDS: 'Founder records',
  MANUAL: 'Entered by a person',
  DERIVED: 'Formula',
};

export const GROUP_LABELS: Readonly<Record<MetricGroup, string>> = {
  REVENUE: 'Revenue and growth',
  CUSTOMERS: 'Customers',
  MARGINS: 'Margins',
  COSTS_CASH: 'Costs, burn and cash',
  BALANCE_SHEET: 'Debt and balance sheet',
  CONTRACTS: 'Contracts and pipeline',
  UNIT_ECONOMICS: 'Unit economics',
  PRODUCT: 'Product',
};

export const WARNING_SEVERITY: Readonly<Record<ValuationWarning['severity'], Display>> = {
  WARNING: { label: 'Warning', tone: 'warn' },
  INFO: { label: 'Note', tone: 'info' },
};

export const SCENARIO_LABELS = { LOW: 'Low', BASE: 'Base', HIGH: 'High' } as const;

/**
 * Whole-unit decimal string ("-2840000.0000") grouped without floats ("-2,840,000"); a value with
 * a non-zero fraction keeps its decimals rather than being truncated.
 */
export function formatWhole(amount: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (match === null) return amount;
  const [, sign = '', whole = '0', fraction = ''] = match;
  if (/[1-9]/.test(fraction)) return formatAmount(amount);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${/^0+$/.test(whole) ? '' : sign}${grouped}`;
}

/** A metric value in its unit, e.g. "12,000.00 USD", "25 %", "14 months", "3.2×". */
export function formatMetricValue(metric: Pick<MetricView, 'value' | 'unit' | 'currency'>): string {
  const value = metric.value;
  if (value === null) return '—';
  switch (metric.unit) {
    case 'MONEY':
      return `${formatAmount(value)}${metric.currency !== null ? ` ${metric.currency}` : ''}`;
    case 'PERCENT':
      return formatPercent(value);
    case 'COUNT':
      return formatWhole(value);
    case 'RATIO':
      return `${trimDecimal(value)}×`;
    case 'MONTHS':
      return `${trimDecimal(value)} months`;
  }
}

function trimDecimal(value: string): string {
  const [whole = value, fraction] = value.split('.');
  const trimmed = fraction?.replace(/0+$/, '') ?? '';
  return trimmed === '' ? whole : `${whole}.${trimmed}`;
}

/** "3.5000" → "3.5×". */
export function formatMultiple(value: string): string {
  return `${trimDecimal(value)}×`;
}

/** One line identifying a comparable set in a picker. */
export function setLabel(set: ComparableSet): string {
  return [
    set.name,
    `${formatMultiple(set.lowMultiple)} / ${formatMultiple(set.baseMultiple)} / ${formatMultiple(set.highMultiple)}`,
    set.geography,
    `as of ${formatDate(set.asOf)}`,
  ].join(' · ');
}
